// Monthly posts view card (feature 007-monthly-posts-view). A compact 4-line variant of the
// week card for the dense month grid: presentational only, takes a WeekPostCardModel + optional
// action callbacks (no Redux/FullCalendar access) so it is unit-testable in isolation.
import React from "react";
import {Dropdown} from "react-bootstrap";
import {getHandlerIconByCode} from "../../helpers/functions";
import {PeerActivityStatus} from "../../redux/Content/PeerActivityStatus/types";
import {WeekPostCardModel} from "./WeekPostCard.helpers";
import {PostType} from "./postType";

interface Props {
    model: WeekPostCardModel;
    onEdit?: () => void;
    // Kept for API compatibility with the board; the month card opens the edit modal on tap,
    // and duplicate/delete/retry live inside that modal rather than on the compact card.
    onDuplicate?: () => void;
    onDelete?: () => void;
    onRetry?: () => void;
}

// Bottom-left corner type glyph: a remix icon + colour class per coarse post type.
const TYPE_ICON: Record<PostType, string> = {
    post: "ri-layout-grid-fill",
    reel: "ri-film-fill",
    story: "ri-record-circle-line",
    video: "ri-vidicon-fill",
};
const TYPE_LABEL: Record<PostType, string> = {
    post: "Post",
    reel: "Reel",
    story: "Story",
    video: "Video",
};

// A short, derived status label for the compact pill; falls back to the model's own label.
function shortStatusLabel(model: WeekPostCardModel): string {
    switch (model.status) {
        case PeerActivityStatus.STATUS_ON_PUBLISH:
        case PeerActivityStatus.STATUS_NEW:
        case PeerActivityStatus.STATUS_READY:
            return "Scheduled";
        case PeerActivityStatus.STATUS_FINAL:
            return "Published";
        case PeerActivityStatus.STATUS_AWAITING_APPROVAL:
            return "Approval";
        case PeerActivityStatus.STATUS_ERROR:
        case PeerActivityStatus.STATUS_REJECTED:
            return "Failed";
        case PeerActivityStatus.STATUS_UNPUBLISHED:
            return "Draft";
        default:
            return model.statusLabel;
    }
}

const MonthPostCard: React.FC<Props> = ({model, onEdit, onDuplicate, onDelete, onRetry}) => {
    const isError = model.status === PeerActivityStatus.STATUS_ERROR
        || model.status === PeerActivityStatus.STATUS_REJECTED;

    const stop = (e: React.MouseEvent) => e.stopPropagation();
    const open = (e: React.SyntheticEvent) => { e.stopPropagation(); onEdit?.(); };
    const onKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === "Enter" || e.key === " ") { e.preventDefault(); open(e); }
    };

    const rootClass = "monthPostCard"
        + (isError ? " monthPostCard--error" : "")
        + (model.isDraft ? " monthPostCard--draft" : "");

    const typeIcon = TYPE_ICON[model.postType] || TYPE_ICON.post;
    const typeLabel = TYPE_LABEL[model.postType] || TYPE_LABEL.post;
    const statusLabel = shortStatusLabel(model);
    const preview = model.title || model.text;

    return (
        <div className={rootClass} data-status={model.status} data-type={model.postType}
             role="button" tabIndex={0} onClick={open} onKeyDown={onKeyDown}
             title={preview || model.channelName}>
            <span className="monthPostCard__stripe" aria-hidden="true"/>

            {/* header: social logo + posting time (left) · status badge (right). On hover/focus the
                status pill is replaced by a status-coloured dot + the ⋮ manage button. */}
            <div className="monthPostCard__top">
                {model.platformCode
                    ? <img className="monthPostCard__platform rounded-circle" src={getHandlerIconByCode(model.platformCode)} alt={model.platformName}/>
                    : <span className="monthPostCard__platform monthPostCard__platform--fallback rounded-circle" aria-hidden="true">
                        {model.channelInitials || (model.platformName ? model.platformName[0] : "?")}
                    </span>}
                {model.startTime && <span className="monthPostCard__time">{model.startTime}</span>}

                <div className="monthPostCard__topRight">
                    {statusLabel &&
                        <span className={"monthPostCard__statusPill badge badge-soft-" + model.statusClass}>{statusLabel}</span>}
                    <div className="monthPostCard__actions">
                        <span className={"monthPostCard__statusDot monthPostCard__statusDot--" + model.statusClass}
                              title={statusLabel} aria-hidden="true"/>
                        <Dropdown className="monthPostCard__menuWrap" onClick={stop}>
                            <Dropdown.Toggle as="button" type="button" bsPrefix="monthPostCard__menu" aria-label="Post actions">
                                <i className="ri-more-2-fill" aria-hidden="true"/>
                            </Dropdown.Toggle>
                            {/* strategy:fixed so the menu escapes the month grid's overflow:hidden clip */}
                            <Dropdown.Menu align="end" renderOnMount popperConfig={{strategy: "fixed"}} className="monthPostCard__menuList">
                                <Dropdown.Item className="monthPostCard__mi monthPostCard__mi--edit" onClick={() => onEdit?.()}>
                                    <i className="ri-pencil-fill me-2"/>Edit
                                </Dropdown.Item>
                                <Dropdown.Item className="monthPostCard__mi" onClick={() => onDuplicate?.()}>
                                    <i className="ri-file-copy-line me-2"/>Duplicate
                                </Dropdown.Item>
                                {isError &&
                                    <Dropdown.Item className="monthPostCard__mi" onClick={() => onRetry?.()}>
                                        <i className="ri-refresh-line me-2"/>Retry
                                    </Dropdown.Item>}
                                <Dropdown.Divider/>
                                <Dropdown.Item className="monthPostCard__mi text-danger" onClick={() => onDelete?.()}>
                                    <i className="ri-delete-bin-fill me-2"/>Delete
                                </Dropdown.Item>
                            </Dropdown.Menu>
                        </Dropdown>
                    </div>
                </div>
            </div>

            {/* channel name */}
            <div className="monthPostCard__channel text-truncate" title={model.channelName}>{model.channelName}</div>

            {/* text preview */}
            {preview && <div className="monthPostCard__preview text-truncate" title={preview}>{preview}</div>}

            {/* bottom line: post-type glyph (left) + media (right), on one shared row */}
            <div className="monthPostCard__footer">
                <i className={"monthPostCard__type monthPostCard__type--" + model.postType + " " + typeIcon}
                   title={typeLabel} aria-label={typeLabel}/>
                <div className="monthPostCard__media">
                    {model.media.isVideo
                        ? <><i className="ri-play-circle-fill" aria-hidden="true"/> Video</>
                        : model.media.hasMedia
                            ? <><i className="ri-image-2-fill" aria-hidden="true"/> {model.media.count}</>
                            : <span className="monthPostCard__media--none">No media</span>}
                </div>
            </div>
        </div>
    );
};

export default MonthPostCard;
